import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { createSubmissionQueue } from './offline-queue.js';
import { MeterQueueContext } from './meter-queue-context.js';

/** @typedef {import('./offline-queue.js').QueuedReading} QueuedReading */

/**
 * Offline queue of meter readings for the whole app: sends stored submissions on app start and whenever the
 * browser reports 'online' again (same `client_uuid` → idempotent on the server).
 * @param {{ store: import('./offline-queue.js').OfflineStore, children: import('react').ReactNode }} props
 */
export function MeterQueueProvider({ store, children }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [entries, setEntries] = useState(/** @type {QueuedReading[]} */ ([]));
  const queue = useMemo(
    () =>
      createSubmissionQueue({
        store,
        send: (entry) =>
          api.portal.meterReadings.record(entry.reading_id, {
            value: entry.value,
            is_new_meter: Boolean(entry.is_new_meter),
            client_uuid: entry.client_uuid,
            photo: entry.photo ?? null,
            photo_2: entry.photo_2 ?? null,
          }),
      }),
    [api, store],
  );

  useEffect(() => {
    let active = true;
    const unsubscribe = queue.subscribe((next) => active && setEntries(next));
    const flush = async () => {
      try {
        const { sent } = await queue.flush();
        if (sent > 0) queryClient.invalidateQueries({ queryKey: keys.portal.resource('meter-readings') });
      } catch {
        // storage unavailable – nothing to send
      }
    };
    queue
      .list()
      .then((initial) => {
        if (!active) return;
        setEntries(initial);
        if (initial.some((e) => !e.error)) flush();
      })
      .catch(() => {});
    window.addEventListener('online', flush);
    return () => {
      active = false;
      unsubscribe();
      window.removeEventListener('online', flush);
    };
  }, [queue, queryClient]);

  const value = useMemo(() => ({ queue, entries }), [queue, entries]);
  return <MeterQueueContext.Provider value={value}>{children}</MeterQueueContext.Provider>;
}
